// src/dataProvider/index.ts
import simpleRestProvider from "ra-data-simple-rest";
import { httpClient } from "../httpClient";
import keycloak from "../keycloak";

import { challengesResource } from "./resources/challenges";
import { usersResource } from "./resources/users";
import { teamsResource } from "./resources/teams";
import { trainingTemplatesResource } from "./resources/trainingTemplates";
import { eventsResource } from "./resources/events";
import { withGlobalClubFilter } from "./clubScope";
import { calendarFeedsResource } from "./resources/calendarFeeds";
import { shopResource } from "./resources/shop";
import { penaltiesResource } from "./resources/penalties";

const apiUrl = "/api";
const baseProvider = simpleRestProvider(apiUrl, httpClient);

export function buildDataProvider(opts: { clubId: number | null; ready: boolean }) {
  const { clubId, ready } = opts;

  if (!ready) return baseProvider;

  const challenges = challengesResource(clubId);
  const users = usersResource(clubId);
  const teams = teamsResource(clubId);
  const templates = trainingTemplatesResource(clubId);
  const events = eventsResource(clubId);
  const calendarFeeds = calendarFeedsResource(clubId);
  const shop = shopResource(clubId);
  const pen = penaltiesResource(clubId);

  return {
    ...baseProvider,

    // ✅ universelles custom für text/json + FormData + Blob (PDF)
    custom: async (url: string, options: any = {}) => {
      const { method = "GET", body, headers, responseType = "json" } = options;

      const h = new Headers(headers || {});

      // payload bauen
      let payload: any = undefined;
      if (body !== undefined && body !== null) {
        if (body instanceof FormData) {
          payload = body; // Content-Type NICHT setzen
        } else if (typeof body === "string") {
          payload = body;
          if (!h.has("Content-Type")) h.set("Content-Type", "application/json");
        } else {
          payload = JSON.stringify(body);
          if (!h.has("Content-Type")) h.set("Content-Type", "application/json");
        }
      }

      // ------------------------------------------------------------
      // ✅ Blob Support (z.B. PDF Report)
      // react-admins fetchUtils.fetchJson kann kein Blob → wir nutzen fetch,
      // aber mit derselben Auth-Logik wie httpClient (Keycloak refresh + Bearer).
      // ------------------------------------------------------------
      if (responseType === "blob") {
        if (!h.has("Accept")) {
          // PDF ist der Standard bei unseren Reports
          h.set("Accept", "application/pdf");
        }

        // Token refresh + Authorization wie in src/httpClient.ts
        if (keycloak.authenticated) {
          try {
            await keycloak.updateToken(60);
            h.set("Authorization", `Bearer ${keycloak.token}`);
          } catch (err) {
            console.error("[dataProvider.custom] token refresh failed", err);
            throw err;
          }
        }

        // FormData Fix wie im httpClient
        const isFormData = typeof FormData !== "undefined" && payload instanceof FormData;
        if (isFormData) {
          h.delete("Content-Type");
        }

        const resp = await fetch(`${apiUrl}/${url}`, {
          method,
          body: payload,
          headers: h,
        });

        if (!resp.ok) {
          // versuche Fehlerbody lesbar zu machen (json → detail) ansonsten text
          let detail: any = null;
          const ct = resp.headers.get("content-type") || "";
          try {
            if (ct.includes("application/json")) {
              detail = await resp.json();
            } else {
              detail = { detail: await resp.text() };
            }
          } catch {
            detail = { detail: resp.statusText || "Request failed" };
          }
          const err: any = new Error(detail?.detail || detail?.message || resp.statusText);
          err.status = resp.status;
          err.body = detail;
          throw err;
        }

        const blob = await resp.blob();
        return {
          data: blob,
          status: resp.status,
          headers: resp.headers,
        };
      }

      // Default: JSON/Text via bestehendem httpClient
      const res: any = await httpClient(`${apiUrl}/${url}`, {
        method,
        body: payload,
        headers: h,
      });

      if (responseType === "text") {
        // ra fetchJson liefert meist { body } als Rohtext
        return { data: res?.body ?? res?.text ?? "" };
      }

      return { data: res?.json ?? res };
    },

    getList: async (resource: string, params: any) => {
      if (resource === "shop-items") return shop.items.getList(params);
      if (resource === "shop-orders") return shop.orders.getList(params);
      if (resource === "penalty-types") return pen.types.getList();
      if (resource === "penalties") return pen.penalties.getList(params);

      if (resource === "calendar-feeds" || resource === "calendarFeeds") {
        return calendarFeeds.getList(params);
      }
      if (resource === "users/assignable-clubs") {
        const { json } = await httpClient("/api/users/assignable-clubs");
        const data = Array.isArray(json) ? json : [];
        return { data, total: data.length };
      }

      const paramsWithClub = withGlobalClubFilter(params, clubId);

      if (resource === "assignableClubs") {
        const club = paramsWithClub?.filter?.clubId;
        const qs = club ? `?clubId=${encodeURIComponent(club)}` : "";
        const { json } = await httpClient(`/api/users/assignable-clubs${qs}`);
        const data = Array.isArray(json) ? json : json?.data ?? [];
        return { data, total: json?.total ?? data.length };
      }

      if (resource === "users") return users.getList(paramsWithClub);
      if (resource === "teams") return teams.getList(paramsWithClub);
      if (resource === "challenges") return challenges.getList(paramsWithClub);
      if (resource === "assignableRoles") return users.assignableRoles(paramsWithClub);

      if (resource === "events/series") return events.series.getList(paramsWithClub);
      if (resource === "events/occurrences") return events.occurrences.getList(paramsWithClub);

      // ✅ media list: events/occurrences/:id/media
      if (events.matchers.isOccMediaResource(resource)) {
        return events.occurrences.mediaGetList(resource);
      }

      return baseProvider.getList(resource, paramsWithClub);
    },

    getOne: async (resource: string, params: any) => {
      if (resource === "shop-items") return shop.items.getOne(params);
      if (resource === "shop-orders") return shop.orders.getOne(params);
      if (resource === "penalty-types") return pen.types.getOne(params);

      if (resource === "events/series") return events.series.getOne(params);

      // ✅ occurrence show: events/occurrences/:id
      if (resource === "events/occurrences") return events.occurrences.getOne(params);

      if (resource === "training-templates/teams:get") return templates.getTeams(params);

      if (resource === "calendar-feeds") return calendarFeeds.getOne(params);

      if (resource === "users") return users.getOne(params);

      return baseProvider.getOne(resource, params);
    },

    update: async (resource: string, params: any) => {
      if (resource === "shop-items") return shop.items.update(params);
      if (resource === "shop-orders") return shop.orders.update(params);
      if (resource === "penalty-types") return pen.types.update(params);
      if (resource === "penalties") return pen.penalties.update(params);

      if (resource === "events/series") return events.series.update(params);

      if (resource === "calendar-feeds") return calendarFeeds.update(params);

      // ✅ attendance: events/occurrences/:id/attendance
      if (events.matchers.isOccAttendanceResource(resource)) {
        return events.occurrences.setAttendance(resource, params);
      }

      if (resource === "training-templates/teams:set") return templates.setTeams(params);
      return baseProvider.update(resource, params);
    },

    delete: async (resource: string, params: any) => {
      if (resource === "shop-items") return shop.items.delete(params);
      if (resource === "shop-orders") return shop.orders.delete(params);
      if (resource === "penalty-types") return pen.types.delete(params);
      if (resource === "penalties") return pen.penalties.delete(params);

      if (resource === "events/series") return events.series.delete(params);
      if (resource === "calendar-feeds") return calendarFeeds.delete(params);
      if (resource === "users") return users.remove(params);
      return baseProvider.delete(resource, params);
    },

    create: async (resource: string, params: any) => {
      if (resource === "shop-items") return shop.items.create(params);
      if (resource === "shop-orders") return shop.orders.create(params);
      if (resource === "penalty-types") return pen.types.create(params);
      if (resource === "penalties") return pen.penalties.create(params);

      if (resource === "users") return users.createInvite(params);
      if (resource === "users/resendInvite") return users.resendInvite(params);
      if (resource === "users/teamAssignments") return users.teamAssignments(params);

      if (resource === "calendar-feeds") return calendarFeeds.create(params);

      if (resource === "training-templates") return templates.create(params);
      if (resource === "training-templates/clone") return templates.clone(params);

      if (resource === "challenges") return challenges.create(params);

      if (resource === "events/series") return events.series.create(params);

      // ✅ media upload: events/occurrences/:id/media (FormData!)
      if (events.matchers.isOccMediaResource(resource)) {
        return events.occurrences.mediaCreate(resource, params);
      }

      return baseProvider.create(resource, params);
    },

    getMany: async (resource: string, params: any) => {
      const paramsWithClub = withGlobalClubFilter(params, clubId);

      // ✅ DAS ist der Fix: ReferenceArrayInput ruft getMany("teams") auf
      if (resource === "teams") return teams.getMany(paramsWithClub);

      return baseProvider.getMany(resource, paramsWithClub);
    },
  };
}
